// Auth gate — blocks the chrome shell unless the visitor has a JWT, AND
// the JWT is still accepted by the backend.
//
// Read order on mount:
//   1. Pull `token` from localStorage. If empty → redirect to /login
//      preserving the requested URL via ?return_to= so the user lands
//      back where they started after a successful sign-in.
//   2. Probe `/api/auth/me` with the token. On 200 → admit the user.
//      On 401/403 → clear the token and bounce to /login so a stale or
//      revoked JWT can't keep the shell alive.
//   3. While the probe is in flight, render a small "Verifying access…"
//      placeholder. We deliberately do NOT render the shell during this
//      window — fixture fallbacks in api/mock.ts would otherwise paint
//      demo data to a logged-out viewer for ~200 ms.
//
// The backend is the real security boundary (every /api/* route runs
// through requireAuth in packages/server/src/index.ts), so this gate is
// about UX and exposure of the design itself, not data integrity.

import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { CurrentUser } from "@duralex/shared";
import { clearToken, fetchMe, TOKEN_STORAGE_KEY } from "../../api/auth";
import { CurrentUserContext } from "./CurrentUserContext";

type GateState = "checking" | "ok" | "denied";

interface Props {
  children: ReactNode;
}

export function RequireAuth({ children }: Props) {
  const location = useLocation();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [state, setState] = useState<GateState>(() => {
    if (typeof window === "undefined") return "checking";
    return window.localStorage.getItem(TOKEN_STORAGE_KEY) ? "checking" : "denied";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.localStorage.getItem(TOKEN_STORAGE_KEY)) {
      setState("denied");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await fetchMe();
        if (cancelled) return;
        if (result.ok) {
          setUser(result.user);
          setState("ok");
        } else if (result.status === 401 || result.status === 403) {
          // Token rejected by the server - drop it so subsequent checks
          // don't keep retrying with the bad credential.
          clearToken();
          setState("denied");
        } else {
          // Transient failure (5xx). Treat the token as valid for now -
          // the server is the real gate, and bouncing every visitor to
          // /login on a backend hiccup would be worse than letting the
          // existing chrome try and fail per-request. `user` stays null;
          // consumers render a neutral fallback.
          setState("ok");
        }
      } catch {
        if (cancelled) return;
        // Network error reaching /api/auth/me - same trade-off as the
        // 5xx branch: don't punish the user for an upstream hiccup.
        setState("ok");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "checking") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--muted)",
          fontSize: 13,
        }}
      >
        Verifying access…
      </div>
    );
  }

  if (state === "denied") {
    const returnTo = `${location.pathname}${location.search}`;
    const qs = new URLSearchParams({ return_to: returnTo });
    return <Navigate to={`/login?${qs.toString()}`} replace />;
  }

  return <CurrentUserContext.Provider value={user}>{children}</CurrentUserContext.Provider>;
}
